import React, { useEffect, useState } from "react";
import { get, post, del, uploadFile } from "../../../services/api";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";

const Documents = ({ candidateId, token, close }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [docs, setDocs] = useState([]);
  const [preview, setPreview] = useState(null);
  const [sasToken, setSasToken] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [forceRenderKey, setForceRenderKey] = useState(0);

  useEffect(() => {
    getDocs();
    getSasToken();
  }, [candidateId]);

  useEffect(() => {
    return () => {
      setDocs([]);
      setPreview(null);
      setSelectedFiles([]);
    };
  }, []);

  const getSasToken = async () => {
    const response = await get("/api/document/generate-sas-token", token);
    setSasToken(response?.sasToken);
  };

  const getDocs = async () => {
    const response = await get(`/api/document/candidate/${candidateId}`, token);
    // if (response?.length > 0) {
    //   setPreview(response[0]);
    // }
    setDocs(response);
  };

  const handleFileChange = (event) => {
    setSelectedFiles(Array.from(event.target.files));
  };

  const handlePreviewChange = (doc) => {
    setIsLoadingPreview(true);
    setPreview(null); // Remove iframe from DOM

    setTimeout(() => {
      setPreview(doc);
      setForceRenderKey((prev) => prev + 1);
    }, 500);
  };

  const downloadFile = (url, filename) => {
    setIsLoading(true);
    fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const blobURL = window.URL.createObjectURL(blob);
        const tempLink = document.createElement("a");
        tempLink.href = blobURL;
        tempLink.download = filename || "downloaded-file";
        tempLink.click();
        window.URL.revokeObjectURL(blobURL);
        setIsLoading(false);
        toast.success("File downloaded");
      })
      .catch(() => setIsLoading(false));
  };

  const deleteFile = async (docId) => {
    setIsLoading(true);
    await del(`/api/document/${docId}`, token);
    await getDocs();
    setIsLoading(false);
    toast.success("Document deleted successfully");
  };

  const uploadAllFiles = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    for (const file of selectedFiles) {
      const response = await uploadFile(file, token, candidateId);
      const body = {
        candidateId,
        fileName: file.name,
        fileExtention: response?.blobName?.slice(-3),
        fileLink: response?.url,
        fileType: file.type,
      };
      await post("/api/document/candidate", body, token);
    }

    await getDocs();
    setIsLoading(false);
    toast.success("All documents uploaded successfully");
  };

  const isImage = (url) => {
    const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "webp"];
    const extensionMatch = url?.split(".").pop()?.split("?")[0]?.toLowerCase();
    return imageExtensions.includes(extensionMatch);
  };

  return (
    <div className="container-fluid p-0">
      <div className="row">
        {/* Document List */}
        <div className="col-xl-3 col-lg-4">
          <div className="card card-bx profile-card author-profile mb-30">
            <div className="card-body p-0">
              <div className="email-left-box dz-scroll pt-0 ps-0">
                <div className="mail-list rounded">
                  <ul style={{ marginLeft: "10px" }}>
                    {docs.map((doc) => (
                      <li
                        className="w-100 border p-4"
                        style={{ cursor: "pointer" }}
                        key={doc._id}
                      >
                        <i className="fa-regular fa-file align-middle" />
                        <span onClick={() => handlePreviewChange(doc)}>
                          {doc.fileName}{" "}
                        </span>
                        <br />
                        <button
                          className="btn btn-sm btn-danger float-end"
                          onClick={() => deleteFile(doc._id)}
                        >
                          <i className="fa-solid fa-trash" />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            downloadFile(
                              doc.fileLink + "?" + sasToken,
                              doc.fileName
                            )
                          }
                          className="btn btn-primary btn-sm"
                        >
                          <i className="fa-solid fa-download" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* File Preview & Upload Section */}
        <div className="col-xl-9 col-lg-8">
          <div className="card profile-card card-bx mb-30">
            <div className="card-header">
              <h6 className="title">File Preview</h6>
            </div>
            <div className="card-body">
              {preview?.fileLink && sasToken ? (
                <>
                  {isLoadingPreview && (
                    <div className="text-center my-3">
                      <Spinner animation="border" size="sm" />
                      <p>Loading preview...</p>
                    </div>
                  )}
                  {preview && preview !== null && (
                    isImage(preview.fileLink) ? (
                      <img
                        src={preview.fileLink + "?" + sasToken}
                        alt="Preview"
                        style={{ width: "100%", maxHeight: "500px", objectFit: "contain" }}
                        onLoad={() => setIsLoadingPreview(false)}
                      />
                    ) : (
                      <iframe
                        key={forceRenderKey}
                        src={
                          preview?.fileName?.includes(".xlsx") || preview?.fileName?.includes(".docx")
                            ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(preview?.fileLink + "?" + sasToken)}`
                            : preview?.fileName?.includes(".pdf")
                              ? `https://drive.google.com/viewerng/viewer?embedded=true&url=${encodeURIComponent(preview?.fileLink + "?" + sasToken)}`
                              : preview?.fileLink + "?" + sasToken
                        }
                        style={{
                          width: "100%",
                          minHeight: "500px",
                          border: "none",
                          display: isLoadingPreview ? "none" : "block",
                        }}
                        allowFullScreen
                        onLoad={() => setIsLoadingPreview(false)}
                      ></iframe>

                    )
                  )}
                </>
              ) : (
                <p className="text-muted text-center">No document selected for preview</p>
              )}
            </div>

            {/* File Upload Section */}
            <div className="card-footer">
              <div className="mb-3">
                <label className="form-label">Select Files</label>
                <input
                  type="file"
                  className="form-control"
                  multiple
                  onChange={handleFileChange}
                />
              </div>
              <div className="d-flex flex-wrap gap-2 mt-3">
                <button
                  className="btn btn-primary"
                  onClick={uploadAllFiles}
                  disabled={isLoading}
                >
                  {isLoading ? <Spinner size="sm" /> : "Upload"}
                </button>
                <button className="btn btn-danger" type="button" onClick={close}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documents;
